/**
 * The sign-in screen — T204 / T312–T319.
 *
 * ⚠ THE BRANDING ON THIS PAGE IS HIS RULING, NOT A STYLE CHOICE (T319). It carried the CLIENT's
 * name, `Developed by J3M Fabrication LLC`, and the DEVELOPER's logo, while the app's own name
 * appeared nowhere. He saw three drawn options and chose: the page belongs to **Forge Halima**,
 * Cholan Dynamics appears once as **Product of CDPL** at the foot, and the J3M line is gone.
 * *"keep the UI Minimal"* — and no sub-line under the wordmark: *"the user will konw that they
 * have to signin when they see it"*. Do not add a strapline back.
 *
 * The returning-user state (name, PIN boxes) is a STATE OF THIS CARD, not a second layout — he
 * liked that treatment in the option with a split panel, and two layouts is two things to keep
 * true (shape 43).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  SupportAgent as SupportIcon,
  Person as PersonIcon,
  Dialpad as PinIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useAuth, takeLogoutReason } from '../contexts/AuthContext';
import { RADIUS } from '../theme';
import { LoginCredentials } from '../types';
import api from '../services/api';
import cdplLogo from '../assets/cdpl-logo.svg';
import {
  canRememberPassword,
  forgetIdentity,
  getLastMethod,
  getRememberedId,
  getRememberedPassword,
  getPinLengthHint,
  hasBeenAsked,
  hasPinHint,
  markAsked,
  rememberId,
  rememberPassword,
  setLastMethod,
  setPinHint,
  SignInMethod,
} from '../services/rememberMe';

const FALLBACK_ADMIN_EMAIL = 'admin@forgedas.com';
const GREEN = '#1F7A63';

/** T331 — the small uppercase field label the reference layout puts above each input. */
const fieldLabelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: '#94a3b8',
  mb: 0.75,
};

/**
 * A readable name from an identifier: `mitchell.white@j3mfab.com` → `Mitchell White`. It is only
 * ever a GUESS from the local part — the real name lives on the account and this screen has not
 * signed in yet — so it falls back to the identifier itself rather than inventing anything.
 */
export function nameOf(identifier: string): string {
  const local = identifier.split('@')[0];
  const words = local.split(/[._-]+/).filter(Boolean);
  if (!words.length) return identifier;
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** Initials for the returning-user chip — two letters at most, and never an empty circle. */
function initialsOf(identifier: string): string {
  const name = identifier.split('@')[0].replace(/[._-]+/g, ' ').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
  return (letters || '?').toUpperCase();
}

const LoginPage: React.FC = () => {
  const { login, loginWithPin } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [contactWarning, setContactWarning] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* T317 — why this screen is showing. It is set only by a manual logout or by the two-day
     expiry, so if there is no reason the person simply has not signed in yet. */
  const [logoutNotice] = useState<string | null>(() => {
    const reason = takeLogoutReason();
    if (reason === 'expired') return 'You were signed out automatically after 2 days. Please sign in again.';
    if (reason === 'manual') return 'You have been signed out.';
    return null;
  });

  /* ── T313 remember me ────────────────────────────────────────────────────────────────────── */
  const [remembered] = useState<string | null>(() => getRememberedId());
  const [remember, setRemember] = useState<boolean>(() => !!getRememberedId());
  const [prompt, setPrompt] = useState<LoginCredentials | null>(null);

  /* ── T318: the screen opens on the door he last came through ─────────────────────────────── */
  const pinAvailable = hasPinHint(remembered);
  const [mode, setMode] = useState<SignInMethod>(() =>
    pinAvailable && getLastMethod() !== 'password' ? 'pin' : 'password');
  const [pin, setPin] = useState('');
  const pinInputRef = useRef<HTMLInputElement | null>(null);
  /* T326 — the number of boxes IS the length of his PIN, remembered on this device when he set
     it. A fifth box opening after a four-digit PIN is exactly what he reported. With no hint we
     show four, the shortest a PIN may be, and he finishes with the button. */
  const pinLength = getPinLengthHint() || 4;
  const submittingPin = useRef(false);

  const chooseMode = (next: SignInMethod) => {
    setMode(next);
    setLastMethod(next);
    setError(null);
  };

  const [failedAttempt, setFailedAttempt] = useState<{ email: string; password: string } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<LoginCredentials>({
    defaultValues: { email: getRememberedId() || '', password: '' } as LoginCredentials,
  });

  /* The saved password is read through the OS vault, so it arrives asynchronously. */
  useEffect(() => {
    if (!remembered || !canRememberPassword) return;
    let live = true;
    void (async () => {
      const saved = await getRememberedPassword(remembered);
      if (live && saved) setValue('password', saved);
    })();
    return () => { live = false; };
  }, [remembered, setValue]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if ((error || contactWarning) && (name === 'email' || name === 'password')) {
        setError(null);
        setContactWarning(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, error, contactWarning]);

  /** Everything that happens once the server has accepted a password. */
  const finishPasswordLogin = useCallback(async (data: LoginCredentials, keep: boolean) => {
    if (keep) {
      rememberId(data.email);
      await rememberPassword(data.email, data.password);
    } else {
      await forgetIdentity();
    }
    setLastMethod('password');
    await login(data);
  }, [login]);

  const onSubmit = async (data: LoginCredentials) => {
    /* *"when new id is entered promt to ask remember the id and password"* — the question comes
       BEFORE the sign-in, because a successful sign-in leaves this screen immediately and a
       dialog on a page that is unmounting is a dialog nobody answers. */
    const isNewId = data.email.trim() !== (remembered || '');
    if (isNewId && !hasBeenAsked(data.email)) {
      setPrompt(data);
      return;
    }

    setError(null);
    setContactWarning(null);
    setIsLoading(true);

    try {
      await finishPasswordLogin(data, remember);
    } catch (err: any) {
      setFailedAttempt({ email: data.email, password: data.password });
      if (!err.response) {
        setError(`Cannot reach login server. ${err?.message || ''}`.trim());
      } else {
        setError(err.response?.data?.message || err?.message || 'Invalid credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const answerPrompt = async (keep: boolean) => {
    const data = prompt;
    setPrompt(null);
    if (!data) return;
    markAsked(data.email);
    setRemember(keep);
    setError(null);
    setIsLoading(true);
    try {
      await finishPasswordLogin(data, keep);
    } catch (err: any) {
      setFailedAttempt({ email: data.email, password: data.password });
      setError(err.response?.data?.message || err?.message || 'Invalid credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const onPinSubmit = async (e?: React.FormEvent, valueOverride?: string) => {
    e?.preventDefault();
    const value = valueOverride ?? pin;
    /* The auto-submit and the button both land here, and `submittingPin` is why the last
       keystroke cannot fire a second request while the first is still in the air. */
    if (!remembered || value.length < 4 || submittingPin.current) return;
    submittingPin.current = true;
    setError(null);
    setIsLoading(true);
    try {
      await loginWithPin(remembered, value);
      setPinHint(remembered, true);
      setLastMethod('pin');
    } catch (err: any) {
      setPin('');
      const message = err.response?.data?.message || err?.message || 'That PIN is not right.';
      setError(message);
      /* The server refuses to say whether an ID has a PIN, so a refusal here is also how this
         device learns its hint was stale — offer the password instead of a box that cannot work. */
      if (/not right/i.test(message)) {
        setPinHint(remembered, false);
        chooseMode('password');
      }
    } finally {
      submittingPin.current = false;
      setIsLoading(false);
    }
  };

  /** Every digit arrives here. The last one signs in by itself — T326. */
  const onPinChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, pinLength);
    setPin(digits);
    if (digits.length === pinLength) void onPinSubmit(undefined, digits);
  };

  const useAnotherAccount = async () => {
    await forgetIdentity();
    chooseMode('password');
    setValue('email', '');
    setValue('password', '');
    setRemember(false);
  };

  const handleContactAdmin = async () => {
    const attempted = failedAttempt
      || (remembered ? { email: remembered, password: '' } : null)
      || (getValues('email') ? { email: getValues('email'), password: '' } : null);
    if (!attempted) {
      setContactWarning('Please try logging in before contacting Admin.');
      return;
    }

    let adminEmail = FALLBACK_ADMIN_EMAIL;

    try {
      const res = await api.post('/auth/contact-admin', { email: attempted.email });
      if (res.data?.data?.contactEmail) adminEmail = res.data.data.contactEmail;
    } catch (_) {}

    const dateStr = new Date().toLocaleString();
    const subject = encodeURIComponent('Login Issue – Support Required');
    const body = encodeURIComponent(
      `Dear Admin,\n\n` +
        `I am unable to log in to the system.\n\n` +
        `Entered Email ID: ${attempted.email}\n\n` +
        `Kindly verify my credentials and update if required.\n\n` +
        `Date & Time: ${dateStr}\n\n` +
        `Regards,\nUser`
    );

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `mailto:${adminEmail}?subject=${subject}&body=${body}`;
    document.body.appendChild(iframe);
    setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 2000);
  };

  /* ── the switch between the two doors (T318) ────────────────────────────────────────────────
     SHAPE 40 — both halves always RENDER. PIN is disabled, with the reason on it, when this
     device has no PIN for the remembered person; a control that vanishes is indistinguishable
     from one nobody built. */
  const methodTab = (value: SignInMethod, label: string, icon: React.ReactNode, disabled: boolean) => (
    <Button
      onClick={() => chooseMode(value)}
      disabled={disabled}
      startIcon={icon}
      disableRipple
      sx={{
        flex: 1,
        py: 0.85,
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.83rem',
        color: mode === value ? GREEN : '#64748b',
        bgcolor: mode === value ? '#fff' : 'transparent',
        boxShadow: mode === value ? '0 1px 3px rgba(15,23,42,0.10)' : 'none',
        '&:hover': { bgcolor: mode === value ? '#fff' : alpha('#fff', 0.5) },
        '&.Mui-disabled': { color: '#cbd5e1' },
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #F1F5F9 0%, #dbeafe 40%, #E8F7F2 100%)',
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        {/* T331 — the wordmark is INSIDE the card now, as in the layout he sent. The page behind
            it carries nothing at all. */}
        <Box
          sx={{
            p: { xs: 3, sm: 3.5 },
            borderRadius: '12px',
            bgcolor: '#fff',
            border: '1px solid rgba(15,23,42,0.07)',
            boxShadow: '0 24px 48px -24px rgba(15,23,42,0.28)',
          }}
        >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 38, height: 38, borderRadius: '8px',
                background: `linear-gradient(140deg, ${GREEN}, #2AA57F)`,
                color: '#fff', display: 'grid', placeItems: 'center',
                fontWeight: 800, fontSize: '1.05rem',
                boxShadow: '0 8px 18px -8px rgba(31,122,99,0.85)',
              }}
            >
              F
            </Box>
            <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, letterSpacing: '-0.5px', color: '#0F172A' }}>
              Forge <Box component="span" sx={{ color: GREEN }}>Halima</Box>
            </Typography>
          </Box>
          {/* NO sub-line under the wordmark — his ruling, T319, and the reference has none either. */}
        </Box>

        {/* T331 — the heading the reference carries, left-aligned above the fields. */}
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#0F172A', mb: 2 }}>
          {mode === 'pin' && remembered ? 'Welcome back' : 'Sign in'}
        </Typography>
          {logoutNotice && !error && <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>{logoutNotice}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}
          {contactWarning && <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>{contactWarning}</Alert>}

          {/* T318 — the switch, and the side it opens on is the side he left on. */}
          {remembered && (
            <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, mb: 2.5, borderRadius: '8px', bgcolor: alpha('#0F172A', 0.05) }}>
              {methodTab('password', 'Password', <LockIcon sx={{ fontSize: 16 }} />, false)}
              <Tooltip title={pinAvailable ? '' : 'Create a PIN in Settings → Security'} placement="top">
                <Box sx={{ flex: 1, display: 'flex' }}>
                  {methodTab('pin', 'PIN', <PinIcon sx={{ fontSize: 16 }} />, !pinAvailable)}
                </Box>
              </Tooltip>
            </Box>
          )}

          {mode === 'pin' && remembered ? (
            <form onSubmit={onPinSubmit}>
              {/* the returning person, named — the treatment he liked, inside this card */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, mb: 2.5,
                borderRadius: '8px', bgcolor: alpha(GREEN, 0.06), border: `1px solid ${alpha(GREEN, 0.14)}` }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: GREEN, color: '#fff',
                  display: 'grid', placeItems: 'center', fontSize: '0.8rem', fontWeight: 700 }}>
                  {initialsOf(remembered)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  {/* T331 — the CARD's heading already says "Welcome back"; saying it again here
                      was the same words twice in two lines. This names the person instead. */}
                  <Typography sx={{ fontSize: '0.83rem', fontWeight: 700, color: '#0F172A' }}>
                    {nameOf(remembered)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {remembered}
                  </Typography>
                </Box>
              </Box>

              {/* One real input behind the boxes: the boxes are what he sees, the input is what
                  the browser and the password manager understand. */}
              <Box sx={{ position: 'relative', mb: 3 }} onClick={() => pinInputRef.current?.focus()}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  {Array.from({ length: pinLength }).map((_, i) => (
                    <Box key={i}
                      sx={{
                        width: 44, height: 50, borderRadius: '8px', bgcolor: '#fff',
                        border: '1px solid', borderColor: i < pin.length ? GREEN : alpha('#0F172A', 0.10),
                        boxShadow: i === pin.length ? `0 0 0 3px ${alpha(GREEN, 0.12)}` : 'none',
                        display: 'grid', placeItems: 'center', fontSize: '1.4rem', color: '#334155',
                      }}
                    >
                      {i < pin.length ? '•' : ''}
                    </Box>
                  ))}
                </Box>
                <Box
                  component="input"
                  ref={pinInputRef}
                  autoFocus
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label="PIN"
                  value={pin}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPinChange(e.target.value)}
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0,
                    border: 0, outline: 'none', cursor: 'pointer' }}
                />
              </Box>

              {/* SHAPE 40 — the button still RENDERS and still works. The auto-submit is a
                  shortcut, not a replacement: a paste, a password manager, or a PIN whose length
                  this device has never seen all still need something to press. */}
              <Button type="submit" fullWidth variant="contained" size="large" disabled={isLoading || pin.length < 4}>
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Link component="button" type="button" underline="hover" onClick={useAnotherAccount}
                  sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                  Not you?
                </Link>
              </Box>
            </form>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mb: 2 }}>
                <Typography sx={fieldLabelSx}>Email or User ID</Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your email or user ID"
                  type="text"
                  {...register('email', { required: 'Email or User ID is required' })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><PersonIcon sx={{ fontSize: 19, color: '#94a3b8' }} /></InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography sx={fieldLabelSx}>Password</Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { required: 'Password is required' })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><LockIcon sx={{ fontSize: 19, color: '#94a3b8' }} /></InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" tabIndex={-1}>
                          {showPassword ? <VisibilityOff sx={{ fontSize: 19 }} /> : <Visibility sx={{ fontSize: 19 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={<Checkbox size="small" checked={remember} onChange={e => setRemember(e.target.checked)} />}
                  label={
                    <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
                      {canRememberPassword ? 'Remember me' : 'Remember my ID'}
                    </Typography>
                  }
                />
              </Box>

              <Button type="submit" fullWidth variant="contained" size="large" disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </form>
          )}

          {/* T331 — the reference's one row of secondary links, with the dot between them. */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
            <Typography component="a" href="/forgot-password"
              sx={{ fontSize: '0.8rem', color: GREEN, textDecoration: 'none', fontWeight: 600 }}>
              Forgot password?
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#cbd5e1' }}>·</Typography>
            <Link component="button" type="button" underline="hover" onClick={handleContactAdmin}
              sx={{ fontSize: '0.8rem', color: GREEN, fontWeight: 600 }}>
              Contact admin
            </Link>
          </Box>

          {/* T331/T332 — *"bring the CDPL logoin the card itself"*, under a rule, as the layout
              he sent has it.

              ⚠ T332 — THIS ASSET WAS MISREAD, AND TWO DECISIONS WERE BUILT ON THE MISREADING.
              `CDPL LoGo white.svg` is named for the BACKGROUND it belongs on, not the colour of
              its ink: it paints BLACK letters with a blue accent on transparent. Judged from a
              13-pixel-tall render it looked like light ink, so T321 put it on a dark chip and
              T331 forced `filter: brightness(0)` over it — which flattened the shield to a blob
              and made the tagline unreadable. His correction: *"its not dark backgroung, its
              suitable for white backgorund"*. It is used PLAIN now, exactly as he supplied it.
              **Read an asset at the size it will be used, and never recolour a brand mark.** */}
          <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: alpha('#0F172A', 0.07), textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.12em',
              color: '#94a3b8', textTransform: 'uppercase', mb: 1 }}>
              A product of
            </Typography>
            <Box component="img" src={cdplLogo} alt="Cholan Dynamics"
              sx={{ height: 26, objectFit: 'contain', display: 'block', mx: 'auto' }} />
          </Box>
        </Box>
      </Box>

      {/* ── T313: the prompt a NEW ID gets, once ─────────────────────────────────────────────── */}
      <Dialog open={!!prompt} onClose={() => answerPrompt(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remember this sign-in?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            {canRememberPassword
              ? `Keep ${prompt?.email || ''} and its password on this computer so you do not have to type them again? The password is stored by Windows itself, encrypted for your account — never in plain text.`
              : `Keep ${prompt?.email || ''} on this browser so you only have to type your password? A browser cannot store a password safely, so the password is not kept. To sign in faster, set a PIN in Settings → Security.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => answerPrompt(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Not now</Button>
          <Button onClick={() => answerPrompt(true)} variant="contained" sx={{ textTransform: 'none' }}>Remember</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
